import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaWhatsapp, FaPaperPlane, FaUser, FaComments } from 'react-icons/fa';
import { submitContactForm, resetContactForm } from '../../redux/slices/contactUsSlice';
import { fetchAppSettings } from '../../redux/slices/appSettingsSlice';
import { toast } from 'react-toastify';
import './ContactUs.css';

const ContactUs = () => {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const { isSubmitting, submitSuccess, submitError } = useSelector((state) => state.contactUs);
  const { settings, loading: settingsLoading } = useSelector((state) => state.appSettings);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // Fetch app settings on component mount
  useEffect(() => {
    dispatch(fetchAppSettings());
  }, [dispatch]);

  // Reset form on successful submission
  useEffect(() => {
    if (submitSuccess) {
      setFormData({ name: '', email: '', subject: '', message: '' });
      toast.success('تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.');
      dispatch(resetContactForm());
    }
  }, [submitSuccess, dispatch]);

  // Show error toast if submission fails
  useEffect(() => {
    if (submitError) {
      toast.error(submitError);
    }
  }, [submitError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await dispatch(submitContactForm(formData)).unwrap();
    } catch (error) {
      // Error is handled by useEffect above
      console.error('Error submitting contact form:', error);
    }
  };

  // Simplified contact info - only phone
  const contactInfo = [
    {
      icon: <FaPhone />,
      title: 'الهاتف',
      value: settings?.phone || '+20 123 456 7890',
      link: `tel:${settings?.phone || '+201234567890'}`
    }
  ];



  return (
    <div className={`contact-us-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Hero Section */}
      <section className="contact-us-hero">
       <div className="container">
       <div className="contact-us-hero-content">
          <div className="contact-us-hero-text">
            <h1 className="contact-us-hero-title">
              تواصل معنا
              <span className="contact-us-hero-subtitle">نحن هنا لمساعدتك</span>
            </h1>
            <p className="contact-us-hero-description">
              نحن متحمسون لسماع آرائكم وأسئلتكم. لا تتردد في التواصل معنا 
              وسنكون سعداء بمساعدتك في رحلة التعلم.
            </p>
          </div>
          <div className="contact-us-hero-visual">
            <div className="contact-us-hero-image">
              <img src="/student.png" alt="تواصل معنا" className="contact-us-hero-img" />
            </div>
          </div>
        </div>
       </div>
      </section>

      {/* Contact Info Section */}
      <section className="contact-us-info">
        <div className="contact-us-container">
          <div className="contact-us-section-header">
            <h2 className="contact-us-section-title">معلومات التواصل</h2>
            <p className="contact-us-section-description">
              يمكنك الوصول إلينا من خلال أي من هذه الطرق
            </p>
          </div>
          <div className="contact-us-info-grid">
            {contactInfo.map((info, index) => (
              <div key={index} className="contact-us-info-card">
                <div className="contact-us-info-icon">{info.icon}</div>
                <h3 className="contact-us-info-title">{info.title}</h3>
                <a 
                  href={info.link} 
                  className="contact-us-info-value"
                  target={info.link.startsWith('http') ? '_blank' : '_self'}
                  rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                >
                  {info.value}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-us-form-section">
        <div className="contact-us-container">
          <div className="contact-us-form-content">
            <div className="contact-us-form-text">
              <h2 className="contact-us-form-title">أرسل لنا رسالة</h2>
              <p className="contact-us-form-description">
                املأ النموذج أدناه وأرسل لنا رسالتك. نحن هنا لمساعدتك في رحلة التعلم 
                وسنرد عليك في أقرب وقت ممكن مع حلول مخصصة لاحتياجاتك.
              </p>
              
              <div className="contact-us-form-features">
                <div className="contact-us-form-feature">
                  <FaPaperPlane className="contact-us-form-feature-icon" />
                  <span>رد سريع خلال 24 ساعة</span>
                </div>
                <div className="contact-us-form-feature">
                  <FaUser className="contact-us-form-feature-icon" />
                  <span>فريق دعم متخصص ومحترف</span>
                </div>
                <div className="contact-us-form-feature">
                  <FaComments className="contact-us-form-feature-icon" />
                  <span>حلول مخصصة لاحتياجاتك التعليمية</span>
                </div>
                <div className="contact-us-form-feature">
                  <FaEnvelope className="contact-us-form-feature-icon" />
                  <span>تواصل آمن ومشفر</span>
                </div>
                <div className="contact-us-form-feature">
                  <FaClock className="contact-us-form-feature-icon" />
                  <span>دعم متواصل على مدار الساعة</span>
                </div>
              </div>

              <div className="contact-us-form-stats">
                <div className="contact-us-form-stat">
                  <div className="contact-us-form-stat-number">24</div>
                  <div className="contact-us-form-stat-label">ساعة للرد</div>
                </div>
                <div className="contact-us-form-stat">
                  <div className="contact-us-form-stat-number">98%</div>
                  <div className="contact-us-form-stat-label">رضا العملاء</div>
                </div>
                <div className="contact-us-form-stat">
                  <div className="contact-us-form-stat-number">500+</div>
                  <div className="contact-us-form-stat-label">رسالة شهرياً</div>
                </div>
              </div>
            </div>
            <div className="contact-us-form-container">
              <form className="contact-us-form" onSubmit={handleSubmit}>
                <div className="contact-us-form-group">
                  <label htmlFor="name" className="contact-us-form-label">الاسم الكامل</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="contact-us-form-input"
                    required
                  />
                </div>
                <div className="contact-us-form-group">
                  <label htmlFor="email" className="contact-us-form-label">البريد الإلكتروني</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="contact-us-form-input"
                    required
                  />
                </div>
                <div className="contact-us-form-group">
                  <label htmlFor="subject" className="contact-us-form-label">الموضوع</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="contact-us-form-input"
                    required
                  />
                </div>
                <div className="contact-us-form-group">
                  <label htmlFor="message" className="contact-us-form-label">الرسالة</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="contact-us-form-textarea"
                    rows="6"
                    required
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="contact-us-form-submit"
                  disabled={isSubmitting}
                >
                  <FaPaperPlane className="contact-us-form-submit-icon" />
                  {isSubmitting ? 'جاري الإرسال...' : 'إرسال الرسالة'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      
    </div>
  );
};

export default ContactUs; 